import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { scannerApi, apiMessage, extractArray, extractData } from 'shared/api/scanner'
import { useAuth } from 'context/AuthContext'
import { useEvent } from 'context/EventContext'
import { PERMISSIONS, hasPermission } from 'shared/util/permissions'
import { PermissionEmpty, Stepper } from 'shared/components/ui/primitives'
import Button from 'shared/components/ui/button'
import Input from 'shared/components/ui/input'
import Label from 'shared/components/ui/label.js'

function GateOrder() {
  const { eventId } = useParams()
  const { user } = useAuth()
  const { event } = useEvent()
  const [plans, setPlans] = useState([])
  const [coupons, setCoupons] = useState([])
  const [success, setSuccess] = useState(null)
  const [saving, setSaving] = useState(false)
  const [differentReceiver, setDifferentReceiver] = useState(false)
  const [form, setForm] = useState({
    iPlanId: '',
    nTicket: 1,
    sName: '',
    sEmail: '',
    sMobile: '',
    iCouponId: '',
    notifyName: '',
    notifyEmail: '',
    notifyMobile: ''
  })

  useEffect(() => {
    scannerApi
      .plans(eventId)
      .then((res) => {
        setPlans(extractArray(res, ['aPlan', 'aPlans']))
      })
      .catch((err) => toast.error(apiMessage(err)))
    scannerApi
      .coupons(eventId, { nLimit: 50 })
      .then((res) => setCoupons(extractArray(res, ['aCoupon', 'aCoupons', 'aEvent'])))
      .catch(() => {})
  }, [eventId])

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!form.iPlanId || !form.sName || !form.sEmail || !form.sMobile) {
      toast.error('Fill attendee details and choose a plan.')
      return
    }
    setSaving(true)
    try {
      const body = {
        iPlanId: form.iPlanId,
        nTicket: form.nTicket,
        sName: form.sName,
        sEmail: form.sEmail,
        sMobile: form.sMobile,
        sCountryCode: '91',
        oNotifyTo: {
          sEmail: differentReceiver ? form.notifyEmail : form.sEmail,
          sReferenceName: differentReceiver ? form.notifyName : form.sName,
          sMobile: differentReceiver ? form.notifyMobile : form.sMobile,
          sCountryCode: '91'
        }
      }
      if (form.iCouponId) body.iCouponId = form.iCouponId
      const res = await scannerApi.placeOrder(eventId, body)
      setSuccess(extractData(res) || { ok: true })
      toast.success(res?.data?.sMessage || 'Ticket generated')
    } catch (err) {
      toast.error(apiMessage(err))
    } finally {
      setSaving(false)
    }
  }

  if (!hasPermission(user, eventId, PERMISSIONS.CREATE_ORDER)) return <PermissionEmpty />

  if (success) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center px-4 text-center">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-success text-4xl text-light">✓</div>
        <h2 className="mb-2 text-5xl font-bold">Ticket generated</h2>
        <p className="mb-6 text-muted">A complimentary / gate order was created for this event.</p>
        <Button className="w-full" onClick={() => setSuccess(null)}>
          Create another
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-lg px-4 py-5">
      <h2 className="mb-1 text-4xl font-bold">Gate order</h2>
      <p className="mb-5 text-muted">{event?.sTitle}</p>
      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <Label>Plan</Label>
          <select className="field-input" value={form.iPlanId} onChange={(e) => setField('iPlanId', e.target.value)}>
            <option value="">Select plan</option>
            {(Array.isArray(plans) ? plans : []).map((plan) => (
              <option key={plan._id} value={plan._id}>
                {plan.sTitle}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col items-center gap-2 rounded-card bg-surface p-4">
          <p className="mb-0 text-sm text-muted">Quantity</p>
          <Stepper value={form.nTicket} min={1} max={10} onChange={(v) => setField('nTicket', v)} />
        </div>
        <div>
          <Label>Attendee name</Label>
          <Input value={form.sName} onChange={(e) => setField('sName', e.target.value)} />
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={form.sEmail} onChange={(e) => setField('sEmail', e.target.value)} />
        </div>
        <div>
          <Label>Mobile</Label>
          <Input type="tel" value={form.sMobile} onChange={(e) => setField('sMobile', e.target.value)} />
        </div>
        <div>
          <Label>Coupon (optional)</Label>
          <select className="field-input" value={form.iCouponId} onChange={(e) => setField('iCouponId', e.target.value)}>
            <option value="">None</option>
            {coupons.map((coupon) => (
              <option key={coupon._id} value={coupon._id}>
                {coupon.sCode}
              </option>
            ))}
          </select>
        </div>
        <label className="flex min-h-tap items-center gap-3 text-sm">
          <input type="checkbox" checked={differentReceiver} onChange={(e) => setDifferentReceiver(e.target.checked)} />
          Someone else receives the ticket
        </label>
        {differentReceiver && (
          <div className="card-surface space-y-3 p-4">
            <Input placeholder="Receiver name" value={form.notifyName} onChange={(e) => setField('notifyName', e.target.value)} />
            <Input placeholder="Receiver email" value={form.notifyEmail} onChange={(e) => setField('notifyEmail', e.target.value)} />
            <Input placeholder="Receiver mobile" value={form.notifyMobile} onChange={(e) => setField('notifyMobile', e.target.value)} />
          </div>
        )}
        <Button className="w-full" type="submit" disabled={saving}>
          {saving ? 'Creating...' : 'Generate ticket'}
        </Button>
      </form>
    </div>
  )
}

export default GateOrder
